import type {
  ErrorContext,
  OakGoogleClassroomExceptionObject,
  OakGoogleClassroomExceptionOptions,
} from "./shared/types";
import { ErrorSeverity, ExceptionType } from "./shared/types";

/**
 * Custom exception for Oak Google Classroom Addon errors
 */
export class OakGoogleClassroomException extends Error {
  type: ExceptionType;
  code?: string;
  shouldRetry?: boolean;
  severity: ErrorSeverity;
  context?: ErrorContext;
  originalError?: Error;

  constructor(
    message: string,
    type: ExceptionType,
    options?: OakGoogleClassroomExceptionOptions,
  ) {
    super(message);

    // Fix prototype chain for ES5 target (required when extending Error)
    Object.setPrototypeOf(this, OakGoogleClassroomException.prototype);

    this.name = "OakGoogleClassroomException";
    this.type = type;
    this.code = options?.code;
    this.shouldRetry = options?.shouldRetry ?? false;
    this.severity = options?.severity ?? ErrorSeverity.Error;
    this.context = options?.context;
    this.originalError = options?.originalError;

    // Preserve original stack trace
    if (options?.originalError?.stack) {
      this.stack = options.originalError.stack;
    }
  }

  public toObject(): OakGoogleClassroomExceptionObject {
    return {
      name: this.name,
      message: this.message,
      type: this.type,
      code: this.code,
      shouldRetry: !!this.shouldRetry,
      severity: this.severity,
      context: this.context,
      originalError: this.originalError,
    };
  }

  public static GoogleOAuthError(
    message: string,
    operation: string,
    metadata?: Record<string, unknown>,
    originalError?: Error,
    severity: ErrorSeverity = ErrorSeverity.Error,
  ): OakGoogleClassroomException {
    return new OakGoogleClassroomException(message, ExceptionType.GoogleOAuth, {
      context: {
        operation,
        service: ExceptionType.GoogleOAuth,
        metadata,
      },
      severity,
      originalError: originalError ?? new Error(message),
    });
  }

  public static OakGoogleClassroomError(
    message: string,
    functionName: string,
    metadata?: Record<string, unknown>,
    originalError?: Error,
    severity: ErrorSeverity = ErrorSeverity.Error,
  ): OakGoogleClassroomException {
    return new OakGoogleClassroomException(
      message,
      ExceptionType.OakGoogleClassroom,
      {
        context: {
          operation: functionName,
          service: ExceptionType.OakGoogleClassroom,
          metadata,
        },
        severity,
        originalError: originalError ?? new Error(message),
      },
    );
  }
}
